import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ip_back } from "../config";
import {
  applyKotMatchSubmittedPatch,
  applyKotRoundCompletedPatch,
  canCoachRequestStopRound,
  canPlayerRequestStopRound,
  deriveKotCoachRoundState,
  mergeKotSnapshots,
  pickMatchForKotStopRequest,
  pickPlayerMatchForKotStopRequest,
} from "../utils/kotCoachRound";
import { noteKotSnapshotRevision } from "../utils/sessionSync";

function authHeaders() {
  const token =
    (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  return token
    ? { Authorization: token, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

/**
 * Coach KoT round state + start-round action for planning card or runtime panels.
 *
 * @param {object} params
 * @param {import("socket.io-client").Socket|null} params.socket
 * @param {string|null} params.teId
 * @param {string|null} params.tsId
 * @param {string|null} [params.exerciseStateId]
 * @param {boolean} [params.enabled]
 * @param {object|null} [params.externalSnapshot] — when set, derived state reads this instead of internal snapshot
 * @param {(payload: object) => void} [params.onSnapshot] — called on socket snapshot / round events
 */
export function useKotCoachRound({
  socket,
  teId,
  tsId,
  exerciseStateId = null,
  enabled = true,
  externalSnapshot = null,
  onSnapshot = null,
  stopRoundMode = "coach",
  viewerPlayerId = null,
}) {
  const [internalSnapshot, setInternalSnapshot] = useState(null);
  const [startingRound, setStartingRound] = useState(false);
  const [startError, setStartError] = useState(null);
  const [stoppingRound, setStoppingRound] = useState(false);
  const [stopRoundError, setStopRoundError] = useState(null);
  const onSnapshotRef = useRef(onSnapshot);
  onSnapshotRef.current = onSnapshot;
  const snapshotDebounceRef = useRef(null);
  const parentOwnsSnapshot = externalSnapshot != null;

  const snapshot = externalSnapshot ?? internalSnapshot;

  const commitSnapshot = useCallback((payload) => {
    if (!payload) return;
    if (payload.kot_snapshot_revision != null && payload.te_id) {
      noteKotSnapshotRevision(payload.te_id, payload.kot_snapshot_revision);
    }
    if (externalSnapshot != null && onSnapshotRef.current) {
      onSnapshotRef.current((prev) => mergeKotSnapshots(prev, payload));
    } else {
      setInternalSnapshot((prev) => mergeKotSnapshots(prev, payload));
    }
  }, [externalSnapshot]);

  const requestSnapshot = useCallback(
    (opts = {}) => {
      if (!enabled || !socket || !tsId || !teId) return;
      if (parentOwnsSnapshot && !opts.force) return;

      const emitPull = () => {
        socket.emit("kot:request-snapshot", { ts_id: tsId, te_id: teId });
        if (opts.httpFallback) {
          fetch(`${ip_back}api/kot/snapshot`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({ ts_id: tsId, te_id: teId }),
          })
            .then((r) => r.json())
            .then((json) => {
              if (json?.data) commitSnapshot(json.data);
            })
            .catch(() => {});
        }
      };

      if (opts.immediate) {
        if (snapshotDebounceRef.current) {
          clearTimeout(snapshotDebounceRef.current);
          snapshotDebounceRef.current = null;
        }
        emitPull();
        return;
      }

      if (snapshotDebounceRef.current) {
        clearTimeout(snapshotDebounceRef.current);
      }
      snapshotDebounceRef.current = setTimeout(() => {
        snapshotDebounceRef.current = null;
        emitPull();
      }, 200);
    },
    [enabled, socket, tsId, teId, commitSnapshot, parentOwnsSnapshot]
  );

  useEffect(
    () => () => {
      if (snapshotDebounceRef.current) {
        clearTimeout(snapshotDebounceRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!enabled || !socket || !tsId || !teId) return;

    if (parentOwnsSnapshot) return undefined;

    const matchTe = (payload) =>
      payload &&
      payload.te_id &&
      String(payload.te_id) === String(teId);

    const handleSnapshot = (payload) => {
      if (!matchTe(payload)) return;
      commitSnapshot(payload);
      if (payload.snapshot_stale) {
        requestSnapshot({ httpFallback: true, immediate: true });
      }
    };
    const handleRoundCompleted = (payload) => {
      if (!matchTe(payload)) return;
      setInternalSnapshot((prev) =>
        applyKotRoundCompletedPatch(prev, teId, tsId, payload)
      );
    };
    const handleMatchSubmitted = (payload) => {
      if (!matchTe(payload)) return;
      setInternalSnapshot((prev) =>
        applyKotMatchSubmittedPatch(prev, teId, tsId, payload)
      );
    };
    const handleLive = (payload) => {
      if (matchTe(payload)) commitSnapshot(payload);
    };

    socket.on("kot:snapshot", handleSnapshot);
    socket.on("kot:round-completed", handleRoundCompleted);
    socket.on("kot:round-started", handleLive);
    socket.on("kot:match-submitted", handleMatchSubmitted);
    socket.on("kot:exercise-completed", handleLive);
    socket.on("kot:round-stop-requested", handleLive);

    requestSnapshot({ httpFallback: true, immediate: true });

    return () => {
      socket.off("kot:snapshot", handleSnapshot);
      socket.off("kot:round-completed", handleRoundCompleted);
      socket.off("kot:round-started", handleLive);
      socket.off("kot:match-submitted", handleMatchSubmitted);
      socket.off("kot:exercise-completed", handleLive);
      socket.off("kot:round-stop-requested", handleLive);
    };
  }, [
    enabled,
    socket,
    tsId,
    teId,
    commitSnapshot,
    requestSnapshot,
    parentOwnsSnapshot,
  ]);

  useEffect(() => {
    if (externalSnapshot == null) {
      setInternalSnapshot(null);
    }
  }, [teId, tsId, externalSnapshot]);

  const derived = useMemo(
    () => deriveKotCoachRoundState(snapshot, exerciseStateId),
    [snapshot, exerciseStateId]
  );

  useEffect(() => {
    if (
      parentOwnsSnapshot ||
      !enabled ||
      (!derived.awaitingCoachStart &&
        !derived.roundClosing &&
        !derived.tableDataSyncPending) ||
      derived.canStartRound
    ) {
      return undefined;
    }
    const pollMs =
      derived.roundClosing || derived.allTablesSubmitted ? 1500 : 5000;
    const id = setTimeout(
      () => requestSnapshot({ httpFallback: false }),
      pollMs
    );
    return () => clearTimeout(id);
  }, [
    parentOwnsSnapshot,
    enabled,
    derived.awaitingCoachStart,
    derived.roundClosing,
    derived.tableDataSyncPending,
    derived.canStartRound,
    derived.allTablesSubmitted,
    requestSnapshot,
    snapshot?.kot_snapshot_revision,
  ]);

  const startNextRound = useCallback(() => {
    if (!socket || !tsId || !teId || !derived.awaitingCoachStart) return;
    if (!derived.canStartRound) return;
    setStartingRound(true);
    setStartError(null);
    socket.emit("kot:start-round", { ts_id: tsId, te_id: teId }, (ack) => {
      setStartingRound(false);
      if (ack && ack.ok === false) {
        setStartError(ack.message || ack.code || "start_failed");
        return;
      }
    });
  }, [socket, tsId, teId, derived.awaitingCoachStart, derived.canStartRound]);

  const clearStartError = useCallback(() => setStartError(null), []);

  const canRequestStopRound = useMemo(() => {
    const config = snapshot?.config ?? null;
    if (stopRoundMode === "player" && viewerPlayerId) {
      return canPlayerRequestStopRound(snapshot, config, viewerPlayerId);
    }
    return canCoachRequestStopRound(snapshot, config);
  }, [snapshot, stopRoundMode, viewerPlayerId]);

  const requestStopRound = useCallback(() => {
    if (!socket || !tsId || !teId || !canRequestStopRound) return;
    const match =
      stopRoundMode === "player" && viewerPlayerId
        ? pickPlayerMatchForKotStopRequest(snapshot, viewerPlayerId)
        : pickMatchForKotStopRequest(snapshot);
    if (!match?.kot_round_id || !match.kot_match_id || !match.table_id) {
      setStopRoundError("kotCoachStopRoundNoMatch");
      return;
    }
    const requestedBy =
      stopRoundMode === "player" && viewerPlayerId
        ? String(viewerPlayerId)
        : match.player_a_id || match.player_b_id || null;
    if (!requestedBy) {
      setStopRoundError("kotCoachStopRoundNoMatch");
      return;
    }
    setStoppingRound(true);
    setStopRoundError(null);
    socket.emit(
      "kot:request-stop-round",
      {
        ts_id: tsId,
        te_id: teId,
        kot_round_id: match.kot_round_id,
        kot_match_id: match.kot_match_id,
        table_id: match.table_id,
        requested_by_player_id: requestedBy,
      },
      (ack) => {
        setStoppingRound(false);
        if (!ack || ack.ok === false) {
          setStopRoundError(
            ack?.message || ack?.code || "kotCoachStopRoundFailed"
          );
          return;
        }
      }
    );
  }, [
    socket,
    tsId,
    teId,
    canRequestStopRound,
    snapshot,
    stopRoundMode,
    viewerPlayerId,
  ]);

  const clearStopRoundError = useCallback(() => setStopRoundError(null), []);

  const requestStopRoundForMatch = useCallback(
    (match, callback) => {
      if (!socket || !tsId || !teId || !match?.kot_match_id) {
        callback?.(new Error("missing_match"));
        return;
      }
      const currentRoundState = snapshot?.current_round?.round_state ?? null;
      if (currentRoundState === "STOP_REQUESTED") {
        callback?.(null);
        return;
      }
      const requestedBy = match.player_a_id || match.player_b_id;
      if (!requestedBy) {
        callback?.(new Error("kotCoachStopRoundNoMatch"));
        return;
      }
      setStoppingRound(true);
      setStopRoundError(null);
      socket.emit(
        "kot:request-stop-round",
        {
          ts_id: tsId,
          te_id: teId,
          kot_round_id: match.kot_round_id,
          kot_match_id: match.kot_match_id,
          table_id: match.table_id,
          requested_by_player_id: requestedBy,
        },
        (ack) => {
          setStoppingRound(false);
          if (!ack || ack.ok === false) {
            const errMsg = ack?.message || ack?.code || "kotCoachStopRoundFailed";
            setStopRoundError(errMsg);
            callback?.(new Error(errMsg));
            return;
          }
          callback?.(null);
        }
      );
    },
    [socket, tsId, teId, snapshot]
  );

  return {
    snapshot,
    ...derived,
    startingRound,
    startError,
    startNextRound,
    clearStartError,
    stoppingRound,
    stopRoundError,
    canRequestStopRound,
    requestStopRound,
    requestStopRoundForMatch,
    clearStopRoundError,
    requestSnapshot,
  };
}
